import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    Button, VStack, FormControl, FormLabel, Input, Select, Textarea, NumberInput,
    NumberInputField, Switch, useToast, Spinner, Text, Box, HStack, IconButton, Heading
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import * as alertRuleService from '../../services/alertRuleService';
import * as compoundAlertRuleService from '../../services/compoundAlertRuleService';

const severities = ['low', 'medium', 'high', 'critical'];

function CompoundAlertRuleForm({ isOpen, onClose, onSave, initialData = null }) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [severity, setSeverity] = useState('medium');
    const [notificationMessage, setNotificationMessage] = useState('');
    const [isEnabled, setIsEnabled] = useState(true);
    const [steps, setSteps] = useState([]);

    const [simpleRules, setSimpleRules] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingSimpleRules, setIsFetchingSimpleRules] = useState(false);
    const toast = useToast();
    const isEditing = !!initialData;

    useEffect(() => {
        if (isOpen) {
            const fetchSimpleRules = async () => {
                setIsFetchingSimpleRules(true);
                try {
                    const response = await alertRuleService.fetchAlertRules({ limit: 1000 }); // Fetch all rules
                    setSimpleRules(response.data || []);
                } catch (error) {
                    toast({ title: "Error fetching simple alert rules", status: "error" });
                } finally {
                    setIsFetchingSimpleRules(false);
                }
            };
            fetchSimpleRules();

            if (initialData) {
                setName(initialData.name || '');
                setDescription(initialData.description || '');
                setSeverity(initialData.severity || 'medium');
                setNotificationMessage(initialData.notification_message || '');
                setIsEnabled(initialData.is_enabled !== false);
                setSteps(initialData.steps || []);
            } else {
                // Reset form for new entry
                setName('');
                setDescription('');
                setSeverity('medium');
                setNotificationMessage('');
                setIsEnabled(true);
                setSteps([{ simple_alert_rule_id: '', time_window_seconds: 600 }]); // Start with one empty step
            }
        }
    }, [isOpen, initialData, toast]);

    const handleStepChange = (index, field, value) => {
        const newSteps = [...steps];
        newSteps[index][field] = value;
        setSteps(newSteps);
    };

    const addStep = () => {
        setSteps([...steps, { simple_alert_rule_id: '', time_window_seconds: 600 }]);
    };

    const removeStep = (index) => {
        setSteps(steps.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        const payload = {
            name,
            description,
            severity,
            notification_message: notificationMessage,
            is_enabled: isEnabled,
            steps: steps.map((step, index) => ({
                simple_alert_rule_id: parseInt(step.simple_alert_rule_id, 10),
                step_order: index + 1,
                time_window_seconds: parseInt(step.time_window_seconds, 10),
            })),
        };

        try {
            if (isEditing) {
                await compoundAlertRuleService.updateCompoundAlertRule(initialData.id, payload);
            } else {
                await compoundAlertRuleService.createCompoundAlertRule(payload);
            }
            toast({ title: `Compound rule ${isEditing ? 'updated' : 'created'}`, status: 'success' });
            onSave(); // Refresh the list on the parent component
            onClose();
        } catch (error) {
            toast({ title: 'Submission Error', description: error.message, status: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{isEditing ? 'Edit Compound Alert Rule' : 'Create Compound Alert Rule'}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <FormControl isRequired><FormLabel>Rule Name</FormLabel><Input value={name} onChange={(e) => setName(e.target.value)} /></FormControl>
                        <FormControl><FormLabel>Description</FormLabel><Textarea value={description} onChange={(e) => setDescription(e.target.value)} /></FormControl>
                        <FormControl isRequired><FormLabel>Severity</FormLabel>
                            <Select value={severity} onChange={(e) => setSeverity(e.target.value)}>
                                {severities.map(s => <option key={s} value={s}>{s}</option>)}
                            </Select>
                        </FormControl>
                        <FormControl><FormLabel>Notification Message</FormLabel><Textarea value={notificationMessage} onChange={(e) => setNotificationMessage(e.target.value)} placeholder="e.g., Probable pipe burst detected between [sensor_1] and [sensor_2]." /></FormControl>
                        <FormControl display="flex" alignItems="center"><FormLabel mb="0">Enabled?</FormLabel><Switch isChecked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} /></FormControl>
                        
                        <Box pt={4}>
                            <Heading size="md" mb={4}>Rule Sequence (Steps)</Heading>
                            {isFetchingSimpleRules ? <Spinner /> :
                                <VStack spacing={4} align="stretch">
                                    {steps.map((step, index) => (
                                        <HStack key={index} p={3} borderWidth="1px" borderRadius="md" spacing={3}>
                                            <Text fontWeight="bold">{index + 1}</Text>
                                            <FormControl isRequired>
                                                <FormLabel fontSize="sm">Simple Alert Rule</FormLabel>
                                                <Select
                                                    placeholder="Select a simple rule"
                                                    value={step.simple_alert_rule_id}
                                                    onChange={(e) => handleStepChange(index, 'simple_alert_rule_id', e.target.value)}
                                                >
                                                    {simpleRules.map(rule => (
                                                        <option key={rule.id} value={rule.id}>{rule.name}</option>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <FormControl isRequired>
                                                <FormLabel fontSize="sm">Max Time from Previous (sec)</FormLabel>
                                                <NumberInput 
                                                    min={1} 
                                                    value={step.time_window_seconds}
                                                    isDisabled={index === 0}
                                                    onChange={(valStr, valNum) => handleStepChange(index, 'time_window_seconds', valNum)}
                                                >
                                                    <NumberInputField />
                                                </NumberInput>
                                            </FormControl>
                                            <IconButton
                                                aria-label="Remove Step"
                                                icon={<DeleteIcon />}
                                                colorScheme="red"
                                                onClick={() => removeStep(index)}
                                                alignSelf="flex-end"
                                                isDisabled={steps.length <= 1}
                                            />
                                        </HStack>
                                    ))}
                                    <Button leftIcon={<AddIcon />} onClick={addStep} size="sm">Add Step</Button>
                                </VStack>
                            }
                        </Box>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button colorScheme="teal" mr={3} onClick={handleSubmit} isLoading={isLoading}>Save Rule</Button>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default CompoundAlertRuleForm;